/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users as UsersIcon,
  PlusCircle,
  Search,
  Edit2,
  Trash2,
  Filter,
  X,
  Shield,
  Activity,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { userService } from '../services/userService';
import { roleService } from '../services/roleService';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import { getScopeLevel } from '../utils/scope';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

const SEARCH_DEBOUNCE_MS = 500;
const DEFAULT_LIMIT = 10;

const INITIAL_FILTERS = {
  search: '',
  status: 'All',
  role: 'All',
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const Users = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canViewRoles = can.roles.view();
  const { user: currentUser } = useAuth();
  const scopeLevel = getScopeLevel(currentUser);
  const showAccountColumn = scopeLevel === 'platform';
  const showSubAccountColumn = scopeLevel !== 'sub_account';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_LIMIT, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(INITIAL_FILTERS);

  const [availableRoles, setAvailableRoles] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const paginationRef = useRef(pagination);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  useEffect(() => {
    if (!canViewRoles) return;
    const fetchRoles = async () => {
      try {
        const response = await roleService.getRoles({ limit: 1000 });
        const rolesData = response.data?.roles || response.data?.data || [];
        setAvailableRoles(rolesData);
      } catch (err) {
        console.error('Failed to load roles:', err);
      }
    };
    fetchRoles();
  }, [canViewRoles]);

  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => setDebouncedFilters(filters), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  const requestIdRef = useRef(0);
  const fetchUsers = async (pageOverride) => {
    const currentRequestId = ++requestIdRef.current;
    const currentPagination = paginationRef.current;
    try {
      setLoading(true);
      const params = {
        page: pageOverride ?? currentPagination.page,
        limit: currentPagination.limit,
        search: debouncedFilters.search || undefined,
        status: debouncedFilters.status === 'All' ? undefined : debouncedFilters.status,
        role: debouncedFilters.role === 'All' ? undefined : debouncedFilters.role,
      };

      const response = await userService.getUsers(params);
      if (currentRequestId !== requestIdRef.current) return;

      const usersData = response.data?.users || response.data?.data || (Array.isArray(response.data) ? response.data : []);
      setUsers(usersData);

      const total = response.data?.pagination?.total ?? response.data?.total ?? usersData.length;
      const limit = response.data?.pagination?.limit ?? response.data?.limit ?? currentPagination.limit;
      const totalPages = response.data?.pagination?.totalPages ?? response.data?.totalPages ?? Math.ceil(total / limit);

      setPagination({
        page: pageOverride ?? (response.data?.pagination?.page || response.data?.page || currentPagination.page),
        limit,
        total,
        totalPages,
      });
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      toastService.error(err.response?.data?.message || err.response?.data?.error || 'Failed to load users');
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchUsers(pagination.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, page }));
  };

  const handleClearFilters = () => setFilters(INITIAL_FILTERS);

  const handleDeleteConfirm = async () => {
    try {
      setSubmitting(true);
      await userService.deleteUser(selectedUser.userId);
      toastService.success('User deleted successfully');
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers(pagination.page);
    } catch (err) {
      toastService.error('Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.role !== 'All' ||
    filters.status !== 'All'
  );

  const canManage = can.users.update() || can.users.delete();

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              User Management
            </h1>
            <p className="text-xs text-base-content/50">
              Manage your team, create new users and assign roles.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            {can.users.create() && (
              <button
                onClick={() => navigate('/users/new')}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
              >
                <PlusCircle size={15} />
                <span>New User</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter & Search Bar Card */}
      <div className="glass-card rounded-2xl p-4 shadow-sm border border-base-content/10 space-y-3 relative z-30">
        {/* Main Search and Filter Grid */}
        <div className="grid grid-cols-12 gap-3">
          {/* Search Input */}
          <div className={`relative col-span-12 ${canViewRoles ? 'sm:col-span-6 lg:col-span-6' : 'sm:col-span-9 lg:col-span-9'}`}>
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40" size={16} />
            <input
              type="text"
              placeholder="Search by name, email, username or external ID..."
              className="w-full pl-10 pr-9 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => setFilters(f => ({ ...f, search: '' }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Role Select */}
          {canViewRoles && (
            <div className="col-span-12 sm:col-span-3 lg:col-span-3">
              <select
                className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                value={filters.role}
                onChange={(e) => setFilters(f => ({ ...f, role: e.target.value }))}
              >
                <option value="All">All Roles</option>
                {availableRoles
                  .slice()
                  .sort((a, b) =>
                    (a.displayName || a.name || '').localeCompare(
                      (b.displayName || b.name || ''),
                      undefined,
                      { sensitivity: 'base' }
                    )
                  )
                  .map((role) => (
                    <option key={role.key || role.id} value={role.id}>
                      {role.displayName || role.name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Status Select */}
          <div className="col-span-12 sm:col-span-3 lg:col-span-3">
            <select
              className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <option value="All">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Active Filter Badges Bar */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-content/5 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1 mr-1">
                <Filter size={11} />
                <span>Active:</span>
              </span>

              {filters.search && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Search: "{filters.search}"</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, search: '' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.role !== 'All' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Shield size={10} className="text-primary" />
                  <span>Role: {availableRoles.find(r => r.id === filters.role)?.displayName || availableRoles.find(r => r.id === filters.role)?.name || filters.role}</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, role: 'All' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.status !== 'All' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <Activity size={10} className="text-primary" />
                  <span>Status: {filters.status === 'active' ? 'Active' : filters.status === 'inactive' ? 'Inactive' : filters.status}</span>
                  <button type="button" onClick={() => setFilters(f => ({ ...f, status: 'All' }))} className="hover:text-error">
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-bold text-base-content/40 hover:text-error transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Main Table Card */}
      <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative z-10">
        {loading ? (
          <div className="p-6 space-y-4">
            <div className="h-6 w-32 bg-base-300/60 rounded animate-pulse"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-base-200/50 rounded-xl animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
              <UsersIcon size={28} />
            </div>
            <div>
              <h3 className="text-base font-black text-base-content">
                {hasActiveFilters ? 'No matching users' : 'No users yet'}
              </h3>
              <p className="text-xs text-base-content/40 max-w-sm mt-1">
                {hasActiveFilters
                  ? 'Try adjusting your search criteria, role, or clearing active filters.'
                  : 'Get started by creating your first user record.'}
              </p>
            </div>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={handleClearFilters}
                className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
              >
                Clear all filters
              </button>
            ) : can.users.create() ? (
              <button
                type="button"
                onClick={() => navigate('/users/new')}
                className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
              >
                <PlusCircle size={14} />
                <span>Create User</span>
              </button>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table w-full text-left">
              <thead>
                <tr className="bg-base-200/40 border-b border-base-content/5 text-[10px] font-black uppercase tracking-widest text-base-content/40">
                  <th className="py-3.5 px-5">Display Name</th>
                  <th className="py-3.5 px-4">Email</th>
                  <th className="py-3.5 px-4">Username</th>
                  {showAccountColumn && <th className="py-3.5 px-4">{ACCOUNT_LABEL.singular}</th>}
                  {showSubAccountColumn && <th className="py-3.5 px-4">{SUB_ACCOUNT_LABEL.singular}</th>}
                  <th className="py-3.5 px-4">Security Roles</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4">Created Date</th>
                  {canManage && <th className="py-3.5 px-5 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-base-content/5 text-xs">
                {users.map((user) => (
                  <tr key={user.userId} className="hover:bg-base-200/40 transition-colors group">
                    {/* Display Name */}
                    <td className="py-3.5 px-5 font-bold">
                      <span className="font-bold text-base-content">
                        {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—'}
                      </span>
                    </td>

                    {/* Email */}
                    <td className="py-3.5 px-4">
                      <span className="text-base-content/60 font-medium">
                        {user.email}
                      </span>
                    </td>

                    {/* Username */}
                    <td className="py-3.5 px-4">
                      <span className="text-base-content/60 font-medium">
                        {user.username || '—'}
                      </span>
                    </td>

                    {/* Account */}
                    {showAccountColumn && (
                      <td className="py-3.5 px-4">
                        <span className="text-base-content/60 font-medium">
                          {user.accountName || '—'}
                        </span>
                      </td>
                    )}

                    {/* Sub-Account */}
                    {showSubAccountColumn && (
                      <td className="py-3.5 px-4">
                        <span className="text-base-content/60 font-medium">
                          {user.subAccountName || '—'}
                        </span>
                      </td>
                    )}

                    {/* Roles */}
                    <td className="py-3.5 px-4">
                      <div className="flex flex-wrap gap-1.5">
                        {user.roles?.length > 0 ? (
                          user.roles.map((role, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[9px] font-black uppercase tracking-widest border border-primary/10 shadow-sm"
                              title={role.description || role.displayName}
                            >
                              {role.displayName || role.name || role.key}
                            </span>
                          ))
                        ) : (
                          <span className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest italic">
                            No Roles
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="py-3.5 px-4 text-center">
                      <StatusBadge status={user.status} />
                    </td>

                    {/* Created Date */}
                    <td className="py-3.5 px-4 text-base-content/70 font-medium whitespace-nowrap">
                      {user.createdAt ? (
                        <div
                          className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
                          data-tip={formatDateTime(user.createdAt)}
                        >
                          <span className="cursor-default hover:text-base-content transition-colors">
                            {formatDate(user.createdAt)}
                          </span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>

                    {/* Actions */}
                    {canManage && (
                      <td className="py-3.5 px-5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {can.users.update() && (
                            <div className="tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Edit">
                              <button
                                type="button"
                                onClick={() => navigate(`/users/${user.userId}/edit`)}
                                className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                aria-label="Edit User"
                              >
                                <Edit2 size={15} />
                              </button>
                            </div>
                          )}
                          {can.users.delete() && (
                            <div className="tooltip tooltip-left tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap" data-tip="Delete">
                              <button
                                type="button"
                                onClick={() => { setSelectedUser(user); setShowDeleteModal(true); }}
                                className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                                aria-label="Delete User"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Compact Table Footer with Pagination */}
        {!loading && users.length > 0 && (
          <div className="px-4 py-2.5 border-t border-base-content/5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-base-content/60 font-medium">
              <span className="font-semibold text-base-content">
                {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-semibold text-base-content">{pagination.total}</span> records
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex items-center gap-2.5">
                <span className="text-xs text-base-content/50 font-medium">
                  Page <span className="font-semibold text-base-content">{pagination.page}</span> /{' '}
                  <span className="font-semibold text-base-content">{pagination.totalPages}</span>
                </span>
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={pagination.page === 1 || loading}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Previous Page"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.totalPages || loading}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Next Page"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSelectedUser(null); }}
        title="Delete User"
      >
        <div className="mb-6 space-y-3">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to delete user{' '}
            <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200">
              {selectedUser?.displayName || `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim() || selectedUser?.email}
            </strong>?
          </p>
          <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-xs font-semibold">
            This action cannot be undone. The user will permanently lose access to the system.
          </div>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteConfirm}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {submitting ? 'Deleting...' : 'Delete User'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Users;
